import React, { useEffect, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { AnswerItem, FileAttachment, QuestionValidationTypes, ReduxState } from 'interface';
import { QuestionSLPRef, SLPQuestionType } from '../../questions.index';
import SLPCamera, { SLPCameraRef, SLPCaptureType } from 'utilities/slp/camera/camera.index';
import { extractTitle } from 'scripts/string';
import { createQuestionTitle } from '../questionsTypes.script';

const SLPCameraQuestion: React.ForwardRefRenderFunction<
    QuestionSLPRef,
    ConnectedProps<typeof connector> & SLPQuestionType
> = (props: ConnectedProps<typeof connector> & SLPQuestionType, ref) => {
    React.useImperativeHandle(ref, () => ({
        validate: (e?: boolean) => validate(e),
        submit: (e?: boolean) => onSubmit(e),
    }));
    const lang = props.lang.SLP.questionsTypes;
    const question = props.question;
    const inputRef = useRef<SLPCameraRef>(null);
    const [title, setTitle] = useState(question.title);
    const index = props.answerList?.findIndex((i) => i.question.id === question.id);
    const isExist = index !== undefined && index > -1;
    let capture = SLPCaptureType.environment;
    if (question.validations) {
        const value = question.validations.find((i) => i.title === QuestionValidationTypes.capture)?.value;
        if (value && value.toString() === SLPCaptureType.user) capture = SLPCaptureType.user;
    }

    const [hint, setHint] = useState(question.description);

    useEffect(() => {
        if (question.description)
            setHint(
                createQuestionTitle(
                    question.description,
                    props.answerList,
                    props.answer?.answerer_info,
                    props.form.indicators,
                ),
            );
        setTitle(
            createQuestionTitle(question.title, props.answerList, props.answer?.answerer_info, props.form.indicators),
        );
    }, [props.answerList]);

    function validate(e?: boolean) {
        if (question.is_required && !inputRef.current?.getValue()) {
            if (e) inputRef.current?.setError(lang.cameraEmp);
            return false;
        }
        return true;
    }

    function setAnswer() {
        const value = inputRef.current?.getValue();
        const data = [...props.answerList];
        if (value && validate()) {
            const response: AnswerItem = {
                answer_files: [value],
                question: props.question,
                id: question.id,
                answer: props.answer?.id,
            };
            if (index !== undefined && index > -1) data[index] = response;
            else data.push(response);
        } else if (index !== undefined && index > -1) data.splice(index, 1);
        props.setAnswerList(data);
    }

    function onSubmit(e?: boolean) {
        const value = inputRef.current?.getValue();
        if (value && validate(e) && props.answer && value.id) {
            props.submit({
                answer_files: [value],
                question: question,
                id: new Date().getTime(),
                created_at: new Date().toISOString(),
            });
        }
    }

    let defaultValue: FileAttachment | undefined = undefined;
    if (isExist) {
        const values = props.answerList[index].answer_files;
        if (values?.length) defaultValue = values[0];
    }

    return (
        <SLPCamera
            color={props.form?.answer_color}
            labelColor={props.form?.question_color}
            hint={extractTitle(hint, false, false)}
            ref={inputRef}
            counter={props.counter}
            label={extractTitle(title, false, false)}
            image={question.image_file}
            required={question.is_required}
            onChange={setAnswer}
            capture={capture}
            dir={props.form.direction}
            defaultValue={defaultValue}
            ws={props.form.workspace.id}
        />
    );
};
const mapStateToProps = (state: ReduxState) => ({
    direction: state.direction,
    lang: state.lang,
});

const connector = connect(mapStateToProps, null, null, { forwardRef: true });
export default connector(React.forwardRef(SLPCameraQuestion));
